<template>
<div
    class='b-appointment-qualification qa-appointment-qualification'
    :class='sectionModifier'>
    <h3 class='b-appointment-qualification__title qa-appointment-qualification-title'>
        {{ $t('EVENT.PREVIEW.QUALIFICATION.TITLE') }}
    </h3>
    <span
        v-if='qualification !== null'
        class='b-appointment-qualification__badge qa-appointment-qualification-badge'
        :class='isHonored ? "b-appointment-qualification__badge--honored" : "b-appointment-qualification__badge--not-honored"'>
        {{ badgeText }}
    </span>
    <div class='b-appointment-qualification__buttons'>
        <button
            type='button'
            class='b-appointment-qualification__button b-appointment-qualification__button--yes qa-appointment-qualification-yes'
            :class='{
                "b-appointment-qualification__button--selected": isHonored,
                "b-appointment-qualification__button--muted": isNotHonored,
            }'
            :disabled='isLoading'
            :aria-label='$t("EVENT.PREVIEW.QUALIFICATION.ARIA.YES")'
            :aria-pressed='isHonored ? "true" : "false"'
            @click='qualify(true)'>
            <FwSpinLoader
                v-if='isLoading && pendingValue === true'
                :isActive='true'
                :size='8'
                :colors='yesSpinnerColors'>
            </FwSpinLoader>
            <template v-else>
                <FwIcon
                    icon='ok-circle'
                    size='16'
                    :color='yesIconColor'>
                </FwIcon>
                {{ $t('EVENT.PREVIEW.QUALIFICATION.YES') }}
            </template>
        </button>
        <button
            type='button'
            class='b-appointment-qualification__button b-appointment-qualification__button--no qa-appointment-qualification-no'
            :class='{
                "b-appointment-qualification__button--selected": isNotHonored,
                "b-appointment-qualification__button--muted": isHonored,
            }'
            :disabled='isLoading'
            :aria-label='$t("EVENT.PREVIEW.QUALIFICATION.ARIA.NO")'
            :aria-pressed='isNotHonored ? "true" : "false"'
            @click='qualify(false)'>
            <FwSpinLoader
                v-if='isLoading && pendingValue === false'
                :isActive='true'
                :size='8'
                :colors='noSpinnerColors'>
            </FwSpinLoader>
            <template v-else>
                <FwIcon
                    icon='close-circle'
                    size='16'
                    :color='noIconColor'>
                </FwIcon>
                {{ $t('EVENT.PREVIEW.QUALIFICATION.NO') }}
            </template>
        </button>
    </div>
    <p v-if='isErrorShow' class='b-appointment-qualification__error qa-appointment-qualification-error'>
        {{ $t('EVENT.PREVIEW.QUALIFICATION.ERROR') }}
    </p>
</div>
</template>

<script lang='ts'>
import { Component, Prop, Vue } from 'vue-facing-decorator';
import { TranslateResult } from 'vue-i18n';
import { EventType } from '@/types/Events/Base';
import { EventsWebApi } from '@/api/event/EventsApi';

// FwIcon/FwSpinLoader take color props, so the values the sass block gets from
// the globally injected vars ($main-green, $main-red) are mirrored here for JS
const ICON_COLORS = {
    green: '#27DBBD',
    red: '#EF103C',
    white: '#ffffff',
    gray: '#BEC7D4',
};

const YES_SPINNER_COLORS = [ICON_COLORS.green, ICON_COLORS.green, ICON_COLORS.green];
const NO_SPINNER_COLORS = [ICON_COLORS.red, ICON_COLORS.red, ICON_COLORS.red];

@Component({
    emits: ['qualified'],
})
export default class AppointmentQualification extends Vue {
    @Prop({ type: Object, required: true }) readonly event!: EventType;

    qualification: boolean | null = null;
    pendingValue: boolean | null = null;
    isLoading: boolean = false;
    isErrorShow: boolean = false;

    yesSpinnerColors: Array<string> = YES_SPINNER_COLORS;
    noSpinnerColors: Array<string> = NO_SPINNER_COLORS;

    get isHonored(): boolean {
        return this.qualification === true;
    }

    get isNotHonored(): boolean {
        return this.qualification === false;
    }

    get sectionModifier(): string {
        if (this.isHonored) {
            return 'b-appointment-qualification--honored';
        }
        if (this.isNotHonored) {
            return 'b-appointment-qualification--not-honored';
        }
        return '';
    }

    get badgeText(): TranslateResult {
        return this.isHonored ?
            this.$t('EVENT.PREVIEW.QUALIFICATION.HONORED') :
            this.$t('EVENT.PREVIEW.QUALIFICATION.NOT.HONORED');
    }

    get yesIconColor(): string {
        if (this.isHonored) {
            return ICON_COLORS.white;
        }
        if (this.isNotHonored || this.isLoading) {
            return ICON_COLORS.gray;
        }
        return ICON_COLORS.green;
    }

    get noIconColor(): string {
        if (this.isNotHonored) {
            return ICON_COLORS.white;
        }
        if (this.isHonored || this.isLoading) {
            return ICON_COLORS.gray;
        }
        return ICON_COLORS.red;
    }

    async qualify(value: boolean) {
        if (this.isLoading || this.qualification === value) {
            return;
        }
        this.isLoading = true;
        this.pendingValue = value;
        this.isErrorShow = false;
        try {
            await EventsWebApi.updateEventQualification({ fulfilled: value }, this.event.id);
            this.qualification = value;
            this.$emit('qualified', value);
        } catch (e) {
            this.isErrorShow = true;
        } finally {
            this.isLoading = false;
            this.pendingValue = null;
        }
    }

    created() {
        this.qualification = this.event.appointment_fulfilled ?? null;
    }
}
</script>

<style lang='sass'>
.b-appointment-qualification
    width: 100%
    margin: 20px 0
    padding: 16px
    border: 1px solid #E0E3EA
    border-radius: 10px
    background-color: #F0F2F5

    &--honored
        background-color: rgba(39, 219, 189, 0.12)

    &--not-honored
        background-color: rgba(239, 16, 60, 0.07)

    &__title
        margin: 0
        font-size: 14px
        font-weight: 700
        color: $alt-blue

    &__badge
        display: inline-block
        margin-top: 8px
        padding: 3px 12px
        border-radius: 20px
        font-size: 11px
        font-weight: 700
        color: #fff

        &--honored
            background-color: $main-green

        &--not-honored
            background-color: $main-red

    &__buttons
        display: flex
        gap: 8px
        margin-top: 12px

    &__button
        display: flex
        align-items: center
        justify-content: center
        gap: 8px
        flex: 1 1 50%
        min-height: 48px
        border-radius: 10px
        border: 1px solid transparent
        font-size: 14px
        font-weight: 700
        text-transform: uppercase
        cursor: pointer
        transition: background-color 0.2s, border-color 0.2s, color 0.2s

        &--yes
            border-color: $main-green

            &.b-appointment-qualification__button--selected
                background-color: $main-green
                color: #fff
                box-shadow: 0 0 0 2px $main-green

        &--no
            border-color: $main-red

            &.b-appointment-qualification__button--selected
                background-color: $main-red
                color: #fff
                box-shadow: 0 0 0 2px $main-red

        &--muted
            border-color: $gray
            color: #BEC7D4

        &:not(:disabled):not(.b-appointment-qualification__button--selected):hover
            background-color: #fff

        &:disabled
            cursor: default
            border-color: #cccccc
            color: #BEC7D4

            svg
                fill: #BEC7D4 !important

    &__error
        margin: 8px 0 0
        font-size: 12px
        font-weight: 500
        color: $main-red
</style>
